import { useEffect, useState } from 'react';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';
import { NotifyMeForm } from './components/NotifyMeForm';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

interface LibraryMagnet {
  slug: string;
  title: string;
  badge: string;
  blurb: string | null;
  source_book_title: string | null;
  source_book_author: string | null;
  ee_landing_url: string | null;
  cover_url: string;
}

export default function MagnetLibraryPage() {
  const [magnets, setMagnets] = useState<LibraryMagnet[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retryNonce, setRetryNonce] = useState(0);

  useBrandGtm();

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetchWithRetry(`${API_BASE}/api/public/magnets`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: { magnets: LibraryMagnet[] }) => {
        if (cancelled) return;
        setMagnets(data.magnets ?? []);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => { cancelled = true; };
  }, [retryNonce]);

  if (status === 'loading') {
    return (
      <TakerShell>
        <div style={{ padding: 48, textAlign: 'center' }}>Loading…</div>
      </TakerShell>
    );
  }
  if (status === 'error') {
    return (
      <TakerShell>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p>Couldn't load the library.</p>
          <button onClick={() => setRetryNonce((n) => n + 1)}>Try again</button>
        </div>
      </TakerShell>
    );
  }

  const live = (magnets ?? []).find((m) => m.badge === 'live');
  const comingSoon = (magnets ?? []).filter((m) => m.badge === 'coming_soon');

  return (
    <TakerShell>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '48px 24px' }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, marginBottom: 8 }}>The Bookkeeper's Library</h1>
        <p style={{ color: '#4A4A6A', marginBottom: 32 }}>Free guides for bookkeeping firm owners, distilled from the field's best thinking.</p>

        {live && (
          <a
            href={live.ee_landing_url ?? `/m/${live.slug}`}
            style={{
              display: 'block', border: '2px solid #4F46FF', borderRadius: 18, padding: 28,
              textDecoration: 'none', color: 'inherit', marginBottom: 40,
            }}
          >
            <img
              src={live.cover_url}
              alt={`${live.title} cover`}
              style={{ width: 120, borderRadius: 8, float: 'left', marginRight: 20 }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>{live.title}</h2>
            <p style={{ color: '#4A4A6A', margin: '0 0 12px' }}>{live.blurb}</p>
            <span style={{ color: '#4F46FF', fontWeight: 600 }}>Get it free →</span>
          </a>
        )}

        {comingSoon.length > 0 && (
          <>
            <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', marginBottom: 16 }}>
              More guides — coming soon
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
              {comingSoon.map((m) => (
                <div key={m.slug} style={{ border: '1px solid #e6ebf3', borderRadius: 14, padding: 18 }}>
                  <img
                    src={m.cover_url}
                    alt={`${m.title} cover`}
                    style={{ width: '100%', maxWidth: 100, borderRadius: 6, marginBottom: 10 }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>{m.title}</h3>
                  {m.blurb && <p style={{ fontSize: 13, color: '#4A4A6A', margin: '0 0 4px' }}>{m.blurb}</p>}
                  <NotifyMeForm slug={m.slug} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </TakerShell>
  );
}
